/**
 * Footnotes: escaping, scanning, rendering, and reading the reference-style
 * form other tools emit.
 *
 * Free of React and the DOM on purpose — this is the part where a subtle
 * mistake silently corrupts someone's citations, so it needs to be checkable on
 * its own.
 *
 * ## The format
 *
 * On disk and in the editor, a footnote is pandoc's inline form: `^[note text]`.
 * The alternative markdown offers is a reference (`[^1]`) plus a definition
 * block at the end of the document — two things that must be kept in step.
 * Delete a marker and its definition is orphaned; copy a paragraph and the
 * definition doesn't travel with it; reorder two paragraphs and the numbering
 * has to be rewritten. Each is a synchronisation bug waiting to happen.
 *
 * Inline, the note *is* the marker. Deleting it deletes the note, copying it
 * copies the note, moving it moves the note. There is no second thing to keep
 * in sync because there is no second thing — and since the editor shows the
 * source, there is no second place to edit it either.
 *
 * Reference-style input is still accepted on the way in; see
 * `inlineFootnoteMarkdown` at the bottom of this file.
 *
 * ## Numbering
 *
 * Never stored and never computed here. The visible number comes from a CSS
 * counter (`.footnote-ref` / `.footnote-list` in index.css), which renumbers on
 * insert, delete and reorder with no bookkeeping and no per-keystroke cost.
 */

/** Positions of brackets that form a matched pair. */
function matchedBrackets(text: string): Set<number> {
  const stack: number[] = [];
  const matched = new Set<number>();
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === "[") {
      stack.push(i);
    } else if (text[i] === "]" && stack.length > 0) {
      matched.add(stack.pop()!);
      matched.add(i);
    }
  }
  return matched;
}

/**
 * Escape a note so it can sit inside `^[...]`.
 *
 * Brackets that pair up are left alone, which keeps a markdown link inside a
 * note (`[title](url)`) readable rather than backslash-littered — worth the
 * extra pass, since citations often carry one. Only brackets that would
 * terminate the footnote early, or leave the scanner unbalanced, get escaped.
 */
export function escapeFootnote(text: string): string {
  // A newline would end the inline token, and a footnote is a sentence or two
  // by nature — flatten rather than lose the tail of the note.
  const flat = text.replace(/\s*\n\s*/g, " ");
  const matched = matchedBrackets(flat);
  let out = "";
  for (let i = 0; i < flat.length; i += 1) {
    const ch = flat[i];
    if (ch === "\\") {
      out += "\\\\";
    } else if ((ch === "[" || ch === "]") && !matched.has(i)) {
      out += `\\${ch}`;
    } else {
      out += ch;
    }
  }
  return out;
}

export function unescapeFootnote(text: string): string {
  let out = "";
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === "\\" && i + 1 < text.length) {
      out += text[i + 1];
      i += 1;
    } else {
      out += text[i];
    }
  }
  return out;
}

/**
 * Index of the `]` closing a footnote whose body starts at `start`, or -1.
 * Mirrors `escapeFootnote`: backslash escapes are skipped, and balanced pairs
 * nest rather than terminate.
 */
export function findFootnoteEnd(src: string, start: number): number {
  let depth = 0;
  for (let i = start; i < src.length; i += 1) {
    const ch = src[i];
    if (ch === "\\") {
      i += 1;
    } else if (ch === "[") {
      depth += 1;
    } else if (ch === "]") {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}

/** `marked` tokenizer for `^[...]`. */
export const footnoteTokenizer = {
  name: "footnote",
  level: "inline" as const,
  start: (src: string) => src.indexOf("^["),
  tokenize: (src: string) => {
    if (!src.startsWith("^[")) return undefined;
    const end = findFootnoteEnd(src, 2);
    if (end === -1) return undefined;
    return {
      type: "footnote",
      raw: src.slice(0, end + 1),
      note: unescapeFootnote(src.slice(2, end)),
    };
  },
};

export interface FootnoteEntry {
  /** Character offset of the opening `^`, for scroll-to. */
  pos: number;
  /** Offset just past the closing `]`. */
  end: number;
  content: string;
}

/**
 * Every footnote in the document, in order.
 *
 * A linear scan over the source rather than a walk over a parse tree: the
 * document *is* the source now, so there is no tree, and this stays off the
 * typing path either way — the page recomputes it on the same debounce as word
 * count and the outline.
 *
 * Escapes and balanced brackets are handled by `findFootnoteEnd`, so what
 * counts as a footnote here is exactly what the serializer would write and the
 * highlighter would colour. Three readings of the same text that disagreed
 * would show up as notes that render but don't list, or list but don't
 * highlight.
 */
export function collectFootnotes(markdown: string): FootnoteEntry[] {
  const found: FootnoteEntry[] = [];
  if (!markdown) return found;

  let index = markdown.indexOf("^[");
  while (index !== -1) {
    if (index > 0 && markdown[index - 1] === "\\") {
      index = markdown.indexOf("^[", index + 2);
      continue;
    }
    const close = findFootnoteEnd(markdown, index + 2);
    if (close === -1) {
      index = markdown.indexOf("^[", index + 2);
      continue;
    }
    found.push({
      pos: index,
      end: close + 1,
      content: unescapeFootnote(markdown.slice(index + 2, close)),
    });
    index = markdown.indexOf("^[", close + 1);
  }

  return found;
}

/**
 * Rewrite reference-style footnotes into the inline form.
 *
 * Nothing this app writes uses the reference form, but plenty of things a user
 * might paste do — Obsidian, pandoc, GitHub and most static site generators
 * all emit it. Without this, importing such a document leaves `[^1]` sitting
 * as literal text and the definitions stranded in a block at the bottom.
 *
 * A reference with no matching definition is left exactly as it was: more
 * likely prose that happens to look like a footnote than a footnote whose
 * definition the author forgot to write.
 */
export function inlineFootnoteMarkdown(markdown: string): string {
  if (!markdown.includes("[^")) return markdown;

  const definitions = new Map<string, string>();
  const lines = markdown.split("\n");
  const kept: string[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    const match = /^\[\^([^\]]+)\]:[ \t]?(.*)$/.exec(lines[i]);
    if (!match) {
      kept.push(lines[i]);
      continue;
    }
    let body = match[2];
    // Indented lines below a definition are continuations of it.
    while (i + 1 < lines.length && /^(?: {4}|\t)\S/.test(lines[i + 1])) {
      body += ` ${lines[i + 1].trim()}`;
      i += 1;
    }
    definitions.set(match[1], body.trim());
  }

  if (definitions.size === 0) return markdown;

  const body = kept.join("\n").replace(/\[\^([^\]]+)\]/g, (whole, label: string) => {
    const note = definitions.get(label);
    return note == null ? whole : `^[${escapeFootnote(note)}]`;
  });

  // Removing the definition block usually leaves a run of blank lines behind.
  return `${body.replace(/\n{3,}/g, "\n\n").trimEnd()}\n`;
}
